import { useState } from 'react';
import { setAccountAccess } from '../lib/internal.js';
import { Badge } from './ui.js';

/**
 * The account-level kill switch: disable ALL access (every token and session
 * gets an explicit 403 "account disabled"; inbound ingest is refused) without
 * touching a byte of data — the Neon DB, files, configs, and history stay
 * intact, and re-enabling restores everything instantly. Distinct from the
 * per-capability pause toggles next to it.
 */
export function AccountAccessPanel({
  account,
  onChanged,
}: {
  account: { id: string; name: string; status: string };
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabled = account.status === 'suspended';

  const toggle = async () => {
    const confirmMsg = disabled
      ? `Re-enable "${account.name}"? All users and API tokens regain access immediately.`
      : `Disable "${account.name}"?\n\nEvery user and API token will be blocked with an "account disabled" message and inbound ingest will be refused. NO data is deleted — re-enabling restores everything.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    setError(null);
    try {
      await setAccountAccess(account.id, disabled ? 'reactivate' : 'suspend');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update account access');
    } finally {
      setBusy(false);
    }
  };

  if (account.status === 'pending') return null; // pending accounts go through Approve

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
        Account access
      </div>
      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-sm text-neutral-700">
          {disabled
            ? 'Disabled — all access blocked; data preserved'
            : 'Enabled — users and tokens have normal access'}
        </span>
        <div className="flex items-center gap-2">
          <Badge tone={disabled ? 'rose' : 'emerald'}>{disabled ? 'Disabled' : 'Active'}</Badge>
          <button
            type="button"
            disabled={busy}
            onClick={toggle}
            className={`rounded-md border px-2 py-1 text-xs font-medium transition disabled:opacity-50 ${
              disabled
                ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'border-rose-300 text-rose-700 hover:bg-rose-50'
            }`}
          >
            {busy ? 'Working…' : disabled ? 'Re-enable' : 'Disable account'}
          </button>
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
